import { useAppSelector } from 'hooks/redux';
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import TransactionRulesPreviewAPI from 'app-components/flux/transaction-rules/transactionRulesApi';
import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

import RuleApplication from 'containers/Settings/ChildPages/RulesSimplifi/RuleApplication';
import RulePreviewTxnList from 'containers/Settings/ChildPages/RulesSimplifi/RulePreviewTxnList';
import RuleSummaryRow from 'containers/Settings/ChildPages/RulesSimplifi/RuleSummaryRow';

import { DateTime } from 'luxon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  main: {
    display: 'flex',
    flex: 'none',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  bottomDivider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  conditionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 0,
    '> div+div': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
  conditions: {
    display: 'flex',
    flex: 1,
    stroke: theme.palette.divider,
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`, // Adjusted to use 12px (1.5) for top/bottom and 16px (2) for left/right
    fontWeight: 500,
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  conditionheading: {
    fontWeight: 500,
  },
  ruleApply: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    overflow: 'visible',
  },
}));

const RuleReview: React.FC = () => {
  const { cx, classes } = useStyles();
  const { state } = useStateContext();
  const [previewTransactions, setPreviewTransactions] = useState<TransactionProps[]>([]);
  const [previewTransactionsEndDate, setPreviewTransactionsEndDate] = useState<string>('');
  const { rule } = state;

  const rulePreviewEnabled = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'enableRenamePreview'),
  );

  const luxonDate = DateTime.fromISO(previewTransactionsEndDate);
  const sinceDate = luxonDate.isValid ? luxonDate.toFormat('LLL yyyy') : undefined;

  useEffect(() => {
    const fetchData = async (rule: TransactionRuleType) => {
      TransactionRulesPreviewAPI.fetchPreview({ ...rule, clientId: rule.clientId || uuid() })
        .then((response) => {
          setPreviewTransactions(response.data.transactions);
          setPreviewTransactionsEndDate(response.data.previewTxnsEndDate);
        })
        .catch(() => {});
    };

    if (rule) {
      fetchData(rule);
    }
  }, [rule]);

  return (
    <>
      <Card className={classes.main}>
        <Box className={cx(classes.row, classes.conditionsContainer, classes.bottomDivider)}>
          <Box className={classes.conditions}>
            <Typography className={classes.conditionheading} variant='bodyRegular'>
              If my transaction matches this:
            </Typography>
          </Box>
          <Box className={classes.conditions}>
            <Typography className={classes.conditionheading} variant='bodyRegular'>
              Then make these changes:
            </Typography>
          </Box>
        </Box>
        <Box className={classes.row}>{rule && <RuleSummaryRow rule={rule} />}</Box>
      </Card>
      {rule && (
        <Card className={classes.ruleApply}>
          <RuleApplication rule={rule} sinceDate={sinceDate} />
          {rulePreviewEnabled && <RulePreviewTxnList txns={previewTransactions} txnRule={rule} sinceDate={sinceDate} />}
        </Card>
      )}
    </>
  );
};

export default RuleReview;
